import React, { ChangeEvent, FormEvent } from 'react';
import styled from 'styled-components';
import Row from 'react-bootstrap/Row';
import { v4 as uuidv4 } from 'uuid';

import { UAppContact } from '../../models/UAppContact';
import { useContactsContext } from '../../contexts/ContactsContext';
import { ContactFormForm, FieldCol, SubmitButtonGroup } from '../styled/ContactsForm';
import { StyledFormControl } from '../styled/Inputs';
import BirthdayField from './BirthdayField';
import { FormSubtitle, Separator } from '../styled';
import PhoneFields from './PhoneFields';
import EmailFields from './EmailFields';
import AddressFields from './AddressFields';
import { ContactsButton } from '../styled/ContactsButton';
import { EditableField } from '../../types';

import { TAppContact, TAppDate, TAppName, TAppProfessionalInfo } from 'TProtocol/common/models';

interface Props {
  onSubmit: (contact: UAppContact) => void;
}

export default function RequestForm(props: Props) {
  const contactsContext = useContactsContext();
  const contact = contactsContext.contactToEdit;

  if (!contact) {
    return null;
  }

  const handleChange = <A extends EditableField, K extends keyof Required<TAppContact>[A], V extends Required<TAppContact>[A][K]>(
    value: V,
    fieldType: new () => Required<TAppContact>[A],
    fieldName: A,
    fieldAttribute: K,
    secondaryFieldAttribute?: K,
  ) => {
    const nonOptionalContact: Required<TAppContact> = contact as Required<TAppContact>;

    if (!nonOptionalContact[fieldName]) {
      nonOptionalContact[fieldName] = new fieldType();
    }

    const contactField = nonOptionalContact[fieldName];

    if (contactField) {
      contactField[fieldAttribute] = value;
    }
    if (secondaryFieldAttribute && secondaryFieldAttribute !== '') {
      contactField[secondaryFieldAttribute] = value;
    }

    contactsContext.setContactToEdit(nonOptionalContact);
  };

  const onChange = (contact: UAppContact) => {
    contactsContext.setContactToEdit(contact);
  };

  const handleBirthdayChange = (value: string, fieldAttribute: 'month' | 'day' | 'year') => {
    if (!contact.birthday) {
      contact.birthday = new TAppDate();
    }
    contact.birthday[fieldAttribute] = value;
    if (!contact.birthday.month || contact.birthday.month.length <= 0) {
      if (contact.birthday.day) {
        contact.birthday.day = '';
      }
    }

    contactsContext.setContactToEdit(contact);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    contact.contactId = uuidv4();

    props.onSubmit(contact);
  };

  let jobTitle = '';
  let companyName = '';
  if (contact.primaryProfessionalInfo) {
    jobTitle = contact.primaryProfessionalInfo.jobTitle ?? '';
    companyName = contact.primaryProfessionalInfo.companyName ?? '';
  }

  return (
    <ContactFormContainer data-contact-id={contact.contactId}>
      <ContactFormForm data-form-form={true} onSubmit={handleSubmit}>

        <Separator/>
        <Row data-contact-id={contact.contactId}>
          <FormSubtitle>Name</FormSubtitle>

          <FieldCol>
            <StyledFormControl
              required={true}
              autoComplete="name"
              name="name"
              placeholder="Name"
              defaultValue={contact.name?.fullName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange(e.target.value, TAppName, 'name', 'fullName')
              }
            />
          </FieldCol>
        </Row>


        <PhoneFields isSingle={true} autocomplete={true} contact={contact} onChange={onChange}/>

        <EmailFields isSingle={true} autocomplete={true} contact={contact} onChange={onChange}/>

        <AddressFields isSingle={true} autocomplete={true} simple={true} contact={contact} onChange={onChange}/>

        <FormSubtitle>Professional Info</FormSubtitle>

        <Row>
          <FieldCol>
            <StyledFormControl
              autoComplete="organization"
              name="organization"
              defaultValue={companyName}
              placeholder="Company Name"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange(e.target.value, TAppProfessionalInfo, 'primaryProfessionalInfo', 'companyName')
              }
            />
          </FieldCol>
        </Row>
        <Row>
          <FieldCol>
            <StyledFormControl
              autoComplete="organization-title"
              name="title"
              defaultValue={jobTitle}
              placeholder="Job Title"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange(e.target.value, TAppProfessionalInfo, 'primaryProfessionalInfo', 'jobTitle')
              }/>
          </FieldCol>
        </Row>

        <FormSubtitle>Birthday</FormSubtitle>
        <BirthdayField
          handleBirthdayChange={handleBirthdayChange}
          birthday={contact.birthday}
          label=""
        />

        <Separator/>

        <SubmitButtonGroup>
          <ContactsButton hover={true} type="submit">
            Submit
          </ContactsButton>
        </SubmitButtonGroup>

      </ContactFormForm>

    </ContactFormContainer>
  );
}

const ContactFormContainer = styled.div`
  ${Separator} {
    margin-top: 8px;
  }
`;
